import { alpha, capitalize, Chip, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useGetByDevice } from '@onesource/components';
import type { TContentBlockBase } from '@onesource/schemas';
import { useGetMembersInstructors, useGetSpace, useGetVideoWatchCounts } from '@onesource/services';
import { formatDurationLabel } from '@onesource/utils';
import { useParams } from '@tanstack/react-router';
import { useRef } from 'react';
import { MdAccessTime, MdCheckCircleOutline, MdClose, MdOutlineVisibility } from 'react-icons/md';

import { ContentCategoryBadge } from '#/features/community-hub/components/content-category-badge';
import { useContentRouteOptional } from '#/features/community-hub/hooks/use-content-route';
import {
  useVideoCompletionPercent,
  useVideoProgressTracking,
} from '#/features/community-hub/hooks/use-video-progress-tracking';

interface VideoBlockPlayerProps {
  block: TContentBlockBase;
  lessonId: string;
  /** When provided, a close button is shown (single-block page). Omit for the inline lesson view. */
  onClose?: () => void;
  /**
   * Rendered beside the player, below the header row — the inline-playlist
   * template puts its VideoPlaylist card here so the playlist's top aligns
   * with the player, not the header (RYC pro area Figma, node 4882:2254).
   */
  aside?: React.ReactNode;
  /**
   * Playlist layout (Figma node 4882:2254): transparent section background —
   * the header row aligns with the player's left edge and the aside sits 16px
   * beside the player (tighter than the default 24px).
   */
  flush?: boolean;
  /**
   * Compact card styling for the inline lesson template: tighter padding and a
   * rounded white card so the player sits neatly under the lesson layout
   * instead of spanning the full content width.
   */
  compact?: boolean;
  /**
   * Hero styling for the inline template's above-the-fold player: no white card
   * or padding, so the header line and the rounded player sit directly on the
   * page background (see the RYC® lessons page design).
   */
  hero?: boolean;
}

/**
 * Renders a single Vimeo video block. Shared by every lesson template (inline
 * hero / compact, playlist hero, item page, single-lesson program, event
 * replay), and all progress tracking — view, play, 25/50/75 % milestones and
 * threshold-based completion — is delegated to `useVideoProgressTracking`, so
 * the player and the tracking rules live in exactly one place.
 */
export function VideoBlockPlayer({
  block,
  lessonId,
  onClose,
  compact = false,
  hero = false,
  aside,
  flush = false,
}: VideoBlockPlayerProps) {
  const theme = useTheme();
  const { isMobile } = useGetByDevice();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: instructorsData } = useGetMembersInstructors();
  const instructor = instructorsData?.items?.find(i => String(i.id) === block.instructorId);

  // The watch-count chip is an opt-in per-space setting (ch_spaces.show_watch_count,
  // toggled in the dashboard space dialog) on the top-level program space. Slug
  // routes expose its id via the content-route context; the legacy
  // /community-hub/spaces/$id tree still carries it as the `id` param.
  const contentRoute = useContentRouteOptional();
  const { id: paramSpaceId } = useParams({ strict: false });
  const spaceId = contentRoute?.programId ?? paramSpaceId;
  const { data: space } = useGetSpace(spaceId ?? '');
  const showWatchCount = space?.showWatchCount === true;

  const { data: watchCounts } = useGetVideoWatchCounts(lessonId, showWatchCount);
  const watchCount = watchCounts?.[block.id] ?? 0;

  // Block override → module setting → 100; then the shared tracking rules.
  const completionPercent = useVideoCompletionPercent(block);
  const { markAsWatched } = useVideoProgressTracking({ block, lessonId, completionPercent, iframeRef, containerRef });

  const duration = block.vimeoDurationSeconds;

  // Duration and watched-count render as soft pills — red content on a light
  // red tint (RYC pro area Figma, node 4882:2254).
  const pillChipSx = {
    height: 20,
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.primary.main,
    bgcolor: alpha(theme.palette.primary.main, 0.12),
    '& .MuiChip-icon': { color: theme.palette.primary.main },
  };

  // Hero sits on the page background with no card chrome; compact is the white
  // rounded card used further down the inline lesson page.
  const containerPaddingBottom = hero ? 0 : compact ? 2 : 4;
  const containerPaddingX = hero ? 0 : compact ? { xs: 2, md: 3 } : { xs: 2, md: 6 };

  // Meta line — category, duration, watch count, mark-as-watched and (on the
  // single-block page) the close button. Shared by both header layouts below.
  const metaContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
      {block.contentCategory && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ContentCategoryBadge size="medium" color="primary" category={block.contentCategory} />
          <Typography variant="caption" sx={{ color: theme.palette.grey[600], fontSize: '9px' }}>
            {capitalize(block.contentCategory.toLowerCase())}
          </Typography>
        </Box>
      )}
      {duration && (
        <Chip
          label={formatDurationLabel(block.vimeoDurationSeconds)}
          icon={<MdAccessTime size={13} />}
          size="small"
          sx={pillChipSx}
        />
      )}

      {showWatchCount && watchCount > 0 && (
        <Chip label={`Watched x${watchCount}`} icon={<MdOutlineVisibility size={13} />} size="small" sx={pillChipSx} />
      )}

      {/* Mark as Watched Button */}
      {block.isPublicViewMarkAndWatched && (
        <Button
          variant="text"
          size="small"
          disableRipple
          startIcon={<MdCheckCircleOutline size={12} color={theme.palette.primary.main} />}
          onClick={markAsWatched}
          sx={{
            color: 'text.primary',
            fontSize: 9,
            minWidth: 'auto',
            '& .MuiButton-icon': { mr: 0.5 },
          }}
        >
          {isMobile ? 'Watched' : 'Mark as Watched'}
        </Button>
      )}

      {/* Close button — only on the single-block page */}
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            color: 'text.primary',
            fontSize: 16,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <MdClose />
        </IconButton>
      )}
    </Box>
  );

  const instructorName = instructor ? `${instructor.firstName} ${instructor.lastName}` : '';

  return (
    <Box
      ref={containerRef}
      sx={{
        pt: hero ? 0 : 2,
        pb: containerPaddingBottom,
        px: containerPaddingX,
        background: hero ? 'transparent' : 'white',
        ...(!hero && compact && { borderRadius: '15px', overflow: 'hidden' }),
      }}
    >
      {block.vimeoId ? (
        <Box>
          {/* Video Header Bar. The above-the-fold hero player stacks the title
              on its own line on mobile, with the instructor and the meta line
              beneath it (see the RYC® lessons page design). */}
          {hero && isMobile ? (
            <Box sx={{ mb: flush ? 1.5 : 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 14 }}>
                {block.title || 'Video'}
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 12, color: theme.palette.grey[600] }}>
                  {instructorName}
                </Typography>
                {metaContent}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mb: flush ? 1.5 : 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                // Playlist layout: the meta (category/duration/watched) right-
                // aligns with the player's edge, not the playlist's — reserve
                // the aside column width (220px playlist + 16px gap).
                ...(flush && !!aside && { pr: { md: '236px' } }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: 9, md: 16 } }}>
                  {block.title || 'Video'}
                </Typography>

                {instructor ? <Divider orientation="vertical" flexItem /> : null}

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, fontSize: { xs: 9, md: 16 }, color: theme.palette.grey[600] }}
                >
                  {instructorName}
                </Typography>
              </Box>

              {metaContent}
            </Box>
          )}

          {/* Video Player (with the optional aside — e.g. the playlist — beside it) */}
          <Box sx={{ display: 'flex', gap: flush ? 2 : 3 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                <iframe
                  ref={iframeRef}
                  src={`https://player.vimeo.com/video/${block.vimeoId}?autoplay=0`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '13px',
                  }}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={block.title || 'Video'}
                />
              </Box>
            </Box>
            {aside}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: 400,
            bgcolor: 'grey.200',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: 900,
          }}
        >
          <Typography color="text.secondary">Video not available</Typography>
        </Box>
      )}
    </Box>
  );
}
